import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { 
  Landmark,
  Calendar,
  Clock
} from 'lucide-react';

const SUMMIT_START_DATE = {
  year: 2026,
  month: 4, // April (1-indexed)
  day: 23
};

const SUMMIT_END_DATE = {
  year: 2026,
  month: 4, // April (1-indexed)
  day: 25
};

const SummitWidget = () => {
  const [daysUntil, setDaysUntil] = useState(null);
  const [countdown, setCountdown] = useState(null);

  useEffect(() => {
    calculateCountdown();
    // Update countdown every minute
    const interval = setInterval(calculateCountdown, 60000);
    return () => clearInterval(interval);
  }, []);

  const calculateCountdown = () => {
    // Create date for April 23, 2026 at 00:00 PT (start of summit day)
    const summitStart = new Date(SUMMIT_START_DATE.year, SUMMIT_START_DATE.month - 1, SUMMIT_START_DATE.day, 0, 0, 0, 0);
    const now = new Date();
    const diff = summitStart - now;
    
    if (diff < 0) {
      // Summit has passed or is ongoing
      setDaysUntil(0);
      setCountdown(null);
      return;
    }
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    setDaysUntil(days);
    
    if (days > 30) {
      // If more than 30 days, just show days
      setCountdown(`${days} ${days === 1 ? 'day' : 'days'}`);
    } else if (days > 0) {
      // Show days and hours if less than 30 days
      setCountdown(`${days} ${days === 1 ? 'day' : 'days'} ${hours} ${hours === 1 ? 'hour' : 'hours'}`);
    } else {
      // Show hours and minutes if less than a day
      setCountdown(`${hours} ${hours === 1 ? 'hour' : 'hours'} ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`);
    }
  };

  const formatDateRange = () => {
    const startDate = new Date(SUMMIT_START_DATE.year, SUMMIT_START_DATE.month - 1, SUMMIT_START_DATE.day);
    const endDate = new Date(SUMMIT_END_DATE.year, SUMMIT_END_DATE.month - 1, SUMMIT_END_DATE.day);
    
    // If same month, show "April 23 - 25, 2026"
    if (SUMMIT_START_DATE.month === SUMMIT_END_DATE.month) {
      const monthName = startDate.toLocaleDateString('en-US', { month: 'long' });
      const startDay = startDate.toLocaleDateString('en-US', { day: 'numeric' });
      const endDay = endDate.toLocaleDateString('en-US', { day: 'numeric' });
      const year = SUMMIT_START_DATE.year;
      return `${monthName} ${startDay} - ${endDay}, ${year}`;
    }
    
    // Different months - show full dates
    const startFormatted = startDate.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
    
    const endFormatted = endDate.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
    
    return `${startFormatted} - ${endFormatted}`;
  };

  return (
    <Card className="hover:shadow-xl transition-all duration-300 rounded-3xl border-0 bg-gradient-to-br from-amber-50 to-orange-100 shadow-lg">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center shadow-lg">
              <Landmark className="w-7 h-7 text-white" />
            </div>
            <div>
              <CardTitle className="text-2xl text-gray-900">Next Summit</CardTitle>
              <CardDescription className="text-gray-600">Annual Impact Accelerator Summit</CardDescription>
            </div>
          </div>
          <Calendar className="w-6 h-6 text-amber-600" />
        </div>
      </CardHeader>
      <CardContent>
        <div className="mb-4">
          <div className="flex items-center space-x-2 mb-2">
            <Clock className="w-4 h-4 text-amber-600" />
            <p className="text-sm font-medium text-gray-700">
              {daysUntil !== null && daysUntil > 0 ? `${daysUntil} ${daysUntil === 1 ? 'day' : 'days'} until summit` : 'Coming soon'}
            </p>
          </div>
          <p className="text-lg font-semibold text-gray-900 mb-1">
            {formatDateRange()}
          </p>
          {countdown && (
            <div className="flex items-center space-x-1 text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded-lg inline-flex mb-2">
              <Clock className="w-3 h-3" />
              <span>{countdown}</span>
            </div>
          )}
          <p className="text-xs text-gray-500 italic mt-2">
            Dates subject to change
          </p>
        </div>
        
        <Button 
          disabled
          className="w-full bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed h-12 rounded-2xl shadow-sm hover:bg-gray-100"
        >
          <Landmark className="w-5 h-5 mr-2 opacity-50" />
          RSVP Coming Soon
        </Button>
      </CardContent>
    </Card>
  );
};

export default SummitWidget;

